// journalAdapter — the Journal read's boundary parser. Pure: `unknown` wire
// JSON in, the client's JournalPage (or a Malformed error) out. The wire is
// parsed with the server's own zod schema (`@bilda/app/journal`), then mapped
// to the client's vocabulary: a typed PeriodKey, the assistant's three states
// (written / corrected and locked / corrected and unlocked), resolved links,
// and a day's activity rows or a rollup's children.

export { parseJournalPage, parseSummarizeOutcome };

import {
    type JournalActivityRow,
    type JournalAssistantText,
    type JournalChild as JournalChildDto,
    type JournalLinkTarget,
    type JournalPeriodView,
    journalViewDto,
} from "@bilda/app/journal";
import { Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type {
    AssistantText,
    JournalActivity,
    JournalChild,
    JournalLink,
    JournalPage,
    JournalPeriod,
    JournalScale,
    PeriodKey,
    SummarizeOutcome,
} from "./ports/journal.js";

// A period's key from its scale and first day ("2026-07-01" → July, 2026).
const keyOf = (scale: JournalScale, start: string): PeriodKey | undefined => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(start);
    if (m === null) return undefined;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    switch (scale) {
        case "day":
            return { scale, year, month, day };
        case "month":
            return { scale, year, month };
        case "year":
            return { scale, year };
    }
};

const assistantOf = (a: JournalAssistantText | null): AssistantText | null => {
    if (a === null) return null;
    switch (a.type) {
        case "written":
            return {
                type: "written",
                markdown: a.markdown,
                writtenAt: a.writtenAt,
                readOwnText: a.readOwnText,
            };
        case "corrected":
            return {
                type: a.locked ? "corrected" : "unlocked",
                markdown: a.markdown,
                original: a.original,
                correctedAt: a.correctedAt,
            };
    }
};

const linkOf = (l: JournalLinkTarget): JournalLink => {
    switch (l.type) {
        case "resource_target":
            return {
                type: "resource",
                href: l.href,
                ref: { type: l.resourceKind, id: l.id, title: l.title },
            };
        case "conversation_target":
            return { type: "conversation", href: l.href, id: l.id };
    }
};

// A raw file (not yet a source) has no Journal row kind; it is left out.
const activityOf = (r: JournalActivityRow): readonly JournalActivity[] => {
    const { id, title, at } = r;
    if (r.type === "conversation_activity")
        return [{ kind: "conversation", id, title, at }];
    switch (r.resourceKind) {
        case "source":
        case "material":
            return [{ kind: r.resourceKind, id, title, at }];
        case "export":
            return [{ kind: "package", id, title, at }];
        case "file":
            return [];
    }
};

// A child line leads with the author's own text, like a folded period.
const childOf = (c: JournalChildDto): readonly JournalChild[] => {
    const key = keyOf(c.scale, c.start);
    return key === undefined
        ? []
        : [{ key, markdown: c.ownMarkdown ?? c.assistantMarkdown ?? "" }];
};

const periodOf = (p: JournalPeriodView): readonly JournalPeriod[] => {
    const key = keyOf(p.scale, p.start);
    if (key === undefined) return [];
    return [
        {
            key,
            assistant: assistantOf(p.assistant),
            own: p.own?.markdown ?? "",
            links: p.links.map(linkOf),
            closed: p.closed,
            detail:
                p.detail.type === "activity"
                    ? {
                          type: "activity",
                          activity: p.detail.rows.flatMap(activityOf),
                      }
                    : {
                          type: "children",
                          children: p.detail.children.flatMap(childOf),
                      },
        },
    ];
};

const parseJournalPage = (
    value: unknown,
): Result.Result<JournalPage, ExternalError> => {
    const parsed = journalViewDto.safeParse(value);
    if (!parsed.success)
        return Result.err(mkMalformed(`journal: ${parsed.error.message}`));
    const view = parsed.data;
    return Result.ok({
        scale: view.scale,
        periods: view.periods.flatMap(periodOf),
        assistantReadsOwn: view.ownTextReading === "allowed",
        olderFrom: view.olderFrom,
    });
};

// The on-demand summary's answer: a typed outcome, never discarded.
const parseSummarizeOutcome = (
    raw: unknown,
): Result.Result<SummarizeOutcome, ExternalError> => {
    const type =
        typeof raw === "object" && raw !== null && "type" in raw
            ? raw.type
            : undefined;
    switch (type) {
        case "summary_written":
        case "summary_empty":
        case "summary_refused":
        case "summary_agent_failed":
        case "summary_append_failed":
            return Result.ok({ type });
        default:
            return Result.err(mkMalformed("summarize outcome"));
    }
};
